import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";

/**
 * ScrollExcavator — professional 2D black silhouette.
 * All joints share coordinate anchors so no piece looks detached when rotating.
 * Nested motion.g's rotate around their exact pivot points (px coordinates in
 * the SVG userspace) so the arm articulates like a real excavator.
 */
export default function ScrollExcavator() {
  const { scrollYProgress } = useScroll();
  const reduced = useReducedMotion();

  const x = useTransform(scrollYProgress, [0, 1], ["-8vw", "104vw"]);
  const y = useTransform(scrollYProgress, [0, 0.25, 0.5, 0.75, 1], [0, -1.5, 0, -2, 0]);

  const boomAnim = reduced ? { rotate: 0 } : { rotate: [-4, 3, 9, 4, -4] };
  const stickAnim = reduced ? { rotate: 0 } : { rotate: [-6, 4, 22, 6, -6] };
  const bucketAnim = reduced ? { rotate: 0 } : { rotate: [-12, -4, 26, 8, -12] };
  const tr = { duration: 3.8, ease: "easeInOut", repeat: Infinity, times: [0, 0.28, 0.5, 0.75, 1] };

  // Pivot coordinates (must match SVG paths precisely)
  const BOOM_PIVOT = { x: 152, y: 96 };
  const STICK_PIVOT = { x: 244, y: 44 }; // = boom tip in unrotated local frame
  const BUCKET_PIVOT = { x: 288, y: 132 }; // = stick tip in stick's local frame

  return (
    <motion.div
      aria-hidden="true"
      style={{ x, y }}
      className="pointer-events-none fixed left-0 bottom-2 md:bottom-3 z-30 w-[160px] md:w-[200px] text-slate-950 select-none"
    >
      <svg
        viewBox="0 0 340 220"
        className="w-full h-auto drop-shadow-[0_10px_20px_rgba(2,32,71,0.4)]"
        fill="currentColor"
      >
        {/* Ground shadow */}
        <ellipse cx="120" cy="210" rx="112" ry="4" fill="#0f172a" opacity="0.18" />

        {/* ================= TRACKS ================= */}
        {/* Outer track pod (one solid shape) */}
        <path d="M 12 158 Q 12 140 34 140 L 202 140 Q 224 140 224 158 L 224 190 Q 224 204 202 204 L 34 204 Q 12 204 12 190 Z" />
        {/* Inner tread cutout (white) with teeth */}
        <rect x="34" y="156" width="168" height="18" rx="9" fill="#ffffff" />
        <g fill="currentColor">
          {Array.from({ length: 22 }).map((_, i) => (
            <rect key={i} x={40 + i * 7.5} y="170" width="2.2" height="6" rx="1" />
          ))}
        </g>
        {/* Rolling stripes on lower track */}
        <g className="excavator-tracks" style={{ ["--track-shift"]: reduced ? "0" : "-14px" }}>
          {Array.from({ length: 16 }).map((_, i) => (
            <line
              key={i}
              x1={36 + i * 12}
              y1="194"
              x2={44 + i * 12}
              y2="194"
              stroke="#0f172a"
              strokeOpacity="0.35"
              strokeWidth="2"
              strokeLinecap="round"
            />
          ))}
        </g>
        {/* Idler + drive wheels (large) */}
        <circle cx="40" cy="165" r="16" />
        <circle cx="40" cy="165" r="6" fill="#ffffff" />
        <circle cx="196" cy="165" r="16" />
        <circle cx="196" cy="165" r="6" fill="#ffffff" />
        {/* Middle rollers */}
        {[74, 104, 134, 164].map((cx) => (
          <g key={cx}>
            <circle cx={cx} cy="181" r="6" />
            <circle cx={cx} cy="181" r="2" fill="#ffffff" />
          </g>
        ))}

        {/* ================= CHASSIS + SLEW ================= */}
        {/* Base plate joining tracks to superstructure */}
        <path d="M 22 128 L 214 128 L 224 148 L 12 148 Z" />
        {/* Slew ring */}
        <rect x="58" y="120" width="120" height="10" rx="3" />

        {/* ================= BODY / CAB ================= */}
        {/* Counterweight (back curved block) */}
        <path d="M 40 128 L 40 96 Q 40 84 52 84 L 72 84 L 72 128 Z" />
        {/* Engine housing */}
        <rect x="70" y="94" width="30" height="34" rx="3" />
        {/* Cab with proper A-pillar and slanted front */}
        <path d="M 96 128 L 96 66 Q 96 56 106 54 L 140 44 L 156 60 L 156 128 Z" />
        {/* Cab windows (glass, white cutout) */}
        <path d="M 106 68 L 106 118 L 150 118 L 150 66 L 140 60 L 112 66 Z" fill="#ffffff" />
        {/* Window vertical divider */}
        <rect x="127" y="60" width="4" height="58" />
        {/* Cab horizontal band */}
        <rect x="107" y="98" width="42" height="2" fill="#0f172a" opacity="0.5" />
        {/* Exhaust stack */}
        <rect x="82" y="78" width="4" height="16" rx="1" />
        {/* Beacon */}
        <circle cx="140" cy="42" r="3" fill="#f59e0b" />
        <rect x="139" y="42" width="2" height="8" />

        {/* Boom-anchor bracket on top of cab (integrates cab and boom base) */}
        <path d="M 146 88 L 168 88 L 168 104 L 146 104 Z" />

        {/* ================= ARM (BOOM) ================= */}
        <motion.g animate={boomAnim} transition={tr} style={{ transformOrigin: `${BOOM_PIVOT.x}px ${BOOM_PIVOT.y}px` }}>
          {/* Boom — curved beam from cab shoulder to boom tip.
              The path starts at the pivot (152,96) so it stays visually attached. */}
          <path
            d="M 148 90
               L 158 84
               L 240 40
               L 252 50
               L 252 58
               L 168 106
               L 148 106
               Z"
          />
          {/* Boom hydraulic cylinder — solid beam from base near cab to mid-boom */}
          <rect
            x="140"
            y="112"
            width="86"
            height="8"
            rx="4"
            transform="rotate(-34 140 116)"
          />
          {/* Cylinder rod ends (small circles at both ends) */}
          <circle cx="140" cy="116" r="4" />
          <circle cx="196" cy="76" r="3" />
          {/* Boom pivot pin */}
          <circle cx={BOOM_PIVOT.x} cy={BOOM_PIVOT.y} r="5" fill="#0f172a" />
          <circle cx={BOOM_PIVOT.x} cy={BOOM_PIVOT.y} r="2" fill="#ffffff" />

          {/* Boom-tip pivot bracket (visually joins boom to stick) */}
          <rect x={STICK_PIVOT.x - 8} y={STICK_PIVOT.y - 8} width="16" height="16" rx="3" />

          {/* ================= STICK ================= */}
          <motion.g
            animate={stickAnim}
            transition={tr}
            style={{ transformOrigin: `${STICK_PIVOT.x}px ${STICK_PIVOT.y}px` }}
          >
            {/* Stick beam — angled from stick pivot down to bucket pivot */}
            <path
              d="M 240 38
                 L 250 38
                 L 296 126
                 L 282 134
                 L 236 46
                 Z"
            />
            {/* Stick hydraulic cylinder — from top of boom into upper-stick */}
            <rect
              x="248"
              y="26"
              width="46"
              height="7"
              rx="3"
              transform="rotate(55 248 30)"
            />
            <circle cx="248" cy="30" r="3.5" />
            <circle cx="274" cy="70" r="3" />

            {/* Stick pivot pin */}
            <circle cx={STICK_PIVOT.x} cy={STICK_PIVOT.y} r="4" fill="#0f172a" />
            <circle cx={STICK_PIVOT.x} cy={STICK_PIVOT.y} r="1.6" fill="#ffffff" />

            {/* Bucket cylinder — from top of stick down to bucket linkage.
                Attached to stick so it moves with it. */}
            <rect
              x="272"
              y="80"
              width="34"
              height="6"
              rx="3"
              transform="rotate(60 272 83)"
            />
            <circle cx="272" cy="83" r="2.8" />

            {/* Bucket linkage H-link (small parallelogram) */}
            <path d="M 286 116 L 300 118 L 302 130 L 288 128 Z" />

            {/* Bucket-tip pivot bracket */}
            <rect x={BUCKET_PIVOT.x - 6} y={BUCKET_PIVOT.y - 6} width="12" height="12" rx="2" />

            {/* ================= BUCKET ================= */}
            <motion.g
              animate={bucketAnim}
              transition={tr}
              style={{ transformOrigin: `${BUCKET_PIVOT.x}px ${BUCKET_PIVOT.y}px` }}
            >
              {/* Real excavator bucket:
                  - Wide top opening (where it attaches to stick)
                  - Curved back
                  - Cutting edge at bottom-front with teeth
                  - Thick belly
              */}
              <path
                d="M 282 126
                   L 300 126
                   L 320 138
                   Q 330 148 328 162
                   L 322 172
                   L 314 174
                   L 300 172
                   Q 288 168 282 158
                   Q 276 146 278 134
                   Z"
              />
              {/* Cutting edge (thicker line at the front-bottom) */}
              <path
                d="M 302 172 L 322 170"
                stroke="#0f172a"
                strokeWidth="4"
                strokeLinecap="round"
              />
              {/* Teeth — 3 small triangles pointing down from cutting edge */}
              <path d="M 306 172 L 305 180 L 310 174 Z" />
              <path d="M 314 172 L 313 180 L 318 174 Z" />
              <path d="M 320 170 L 320 178 L 324 172 Z" />
              {/* Bucket pivot pin */}
              <circle cx={BUCKET_PIVOT.x} cy={BUCKET_PIVOT.y} r="3.5" fill="#0f172a" />
              <circle cx={BUCKET_PIVOT.x} cy={BUCKET_PIVOT.y} r="1.5" fill="#ffffff" />
            </motion.g>
          </motion.g>
        </motion.g>
      </svg>
    </motion.div>
  );
}
